import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { landing as landingApi } from '@/lib/api';
import PublicNav, { PublicFooter } from '@/components/PublicNav';
import AnalyticsTracker from '@/components/AnalyticsTracker';
import { ArrowRight, Check, Sparkles, Play, ChevronRight, Zap, Shield, TrendingUp, Code, Layers, BarChart3 } from 'lucide-react';

const iconMap = { zap: Zap, shield: Shield, 'trending-up': TrendingUp, code: Code, layers: Layers, 'bar-chart': BarChart3 };

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);

  useEffect(() => {
    landingApi.get().then(r => setContent(r.data)).catch(() => {});
  }, []);

  if (!content) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-8 h-8 border-2 brand-spinner rounded-full animate-spin" /></div>;
  }

  const hero = content.hero || {};
  const stats = content.stats || [];
  const features = content.features || [];
  const ctaSection = content.cta_section || {};
  const featuresHeading = content.features_heading || {};
  const pricingBand = content.pricing_band || {};
  const footer = content.footer || {};

  return (
    <div data-testid="landing-page" className="light-page min-h-screen bg-white text-slate-900">
      <PublicNav />
      <AnalyticsTracker sections={['hero-section', 'stats-section', 'features-section', 'cta-section']} />

      {/* ── Hero ── */}
      <section data-testid="hero-section" className="relative pt-28 pb-20 md:pt-36 md:pb-28 px-5 overflow-hidden">
        {/* Gradient mesh background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] brand-mesh-1 rounded-full blur-[120px] opacity-60" />
          <div className="absolute top-20 right-1/4 w-[500px] h-[500px] brand-mesh-2 rounded-full blur-[120px] opacity-50" />
          <div className="absolute bottom-0 left-1/2 w-[400px] h-[400px] bg-sky-100 rounded-full blur-[100px] opacity-40" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          {hero.badge && (
            <div data-testid="hero-badge" className="brand-badge inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold mb-8">
              <Sparkles className="w-3 h-3" />{hero.badge}
            </div>
          )}
          <h1 data-testid="hero-title" className="text-4xl md:text-6xl lg:text-[68px] font-extrabold tracking-tight leading-[1.08] text-slate-900 mb-6" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
            {hero.title}
          </h1>
          <p data-testid="hero-subtitle" className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed mb-10">
            {hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              data-testid="hero-cta"
              onClick={() => navigate(user ? '/dashboard' : '/signup')}
              className="brand-btn inline-flex items-center gap-2 px-8 py-3.5 text-base"
            >
              {hero.cta_text || 'Get Started Free'} <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/services-info"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl border border-slate-200 bg-white text-slate-700 text-base font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all"
            >
              {hero.cta_secondary_text || 'Explore Services'} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      {stats.length > 0 && (
        <section data-testid="stats-section" className="py-10 bg-slate-50 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p data-testid={`stat-value-${i}`} className="text-3xl md:text-4xl font-extrabold text-slate-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Value Propositions (concise, conversion-focused) ── */}
      {features.length > 0 && (
        <section data-testid="features-section" className="py-20 md:py-28 px-5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-3" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
                {featuresHeading.title || 'Why teams choose us'}
              </h2>
              <p className="text-slate-500 text-lg max-w-xl mx-auto">{featuresHeading.subtitle || 'Everything you need to ship AI products faster.'}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.slice(0, 6).map((feat, i) => {
                const Icon = iconMap[feat.icon] || Zap;
                return (
                  <div
                    key={i}
                    data-testid={`feature-card-${i}`}
                    className="group p-6 rounded-2xl border border-slate-100 bg-white hover:brand-border-soft hover:shadow-lg hover:brand-shadow transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl brand-icon-bg brand-icon-bg-hover flex items-center justify-center mb-4 transition-colors">
                      <Icon className="w-5 h-5 brand-text" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1.5" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{feat.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{feat.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Pricing band → drive to /pricing ── */}
      <section className="py-16 px-5 brand-gradient-bg relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-sky-300 rounded-full blur-[80px]" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4 tracking-tight" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
            {pricingBand.title || 'Start with just $10'}
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
            {pricingBand.subtitle || 'No subscriptions. No contracts. Add credit to your wallet and pay only for the hours you use.'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            {(pricingBand.chips || ['spaCy NLP: $0.02/hr', 'Redis Cache: $0.03/hr', 'BGE-M3: $0.10/hr', 'Qdrant: $0.06/hr']).map((p, i) => (
              <span key={i} className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium">{p}</span>
            ))}
          </div>
          <Link
            to="/pricing"
            data-testid="view-pricing-cta"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-white brand-text text-base font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
          >
            View Full Pricing <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section data-testid="cta-section" className="py-20 md:py-28 px-5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 data-testid="cta-title" className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-4" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
            {ctaSection.title}
          </h2>
          <p className="text-slate-500 text-lg mb-8 max-w-lg mx-auto">{ctaSection.subtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              data-testid="cta-button"
              onClick={() => navigate(user ? '/dashboard' : '/signup')}
              className="brand-btn inline-flex items-center gap-2 px-8 py-3.5 text-base"
            >
              {ctaSection.button_text || 'Get Started'} <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/services-info"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl border border-slate-200 text-slate-700 text-base font-semibold hover:bg-slate-50 transition-all"
            >
              Learn More <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter footer={footer} />
    </div>
  );
}
